import React, { useEffect, useState } from 'react';
import { techGroups, Experience } from '../utils/Data';
import { AnimatePresence, motion } from 'framer-motion';
import WhatsAppLink from '../utils/WhatsappLink';
import { FaChevronDown, FaRocket, FaLightbulb } from 'react-icons/fa';

function AboutMe() {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // Open the first role by default

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="relative min-h-screen pt-28 pb-16 overflow-hidden bg-grid-pattern">
      <div className="glow-spot top-1/3 right-1/4 translate-x-1/2"></div>

      <div className="max-w-6xl mx-auto w-full px-5 md:px-10 lg:px-24 relative z-10 space-y-16">
        
        {/* Profile Narrative Section */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 text-start items-start">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 space-y-8"
          >
            <div className="space-y-4">
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                About Me
              </h1>
              <div className="h-1.5 w-16 bg-teal-500 rounded-full"></div>
            </div>

            <div className="space-y-6 text-slate-600 dark:text-slate-300 leading-relaxed text-sm md:text-base">
              <p>
                I'm a full-stack web and mobile developer with a background in designing responsive, high-performance web applications. I approach programming not just as writing syntax, but as a discipline for turning complex product specifications into smooth, intuitive user tools.
              </p>
              <p>
                Whether it's optimizing API integrations for speed, crafting fluid UI layers with Framer Motion, or writing secure Solidity smart contracts, I focus on the structural integrity of the code and the final user experience.
              </p>
              <p>
                I am actively scaling my products (like Confess) and diving deeper into Web3 systems, Solidity, and developer tooling to keep my engineering skills sharp and versatile.
              </p>
            </div>

            {/* Intro Blocks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4">
              <div className="p-5 glass rounded-xl space-y-2">
                <div className="flex items-center gap-3 text-teal-600 dark:text-teal-400 font-semibold text-sm">
                  <FaRocket /> <span>What I build</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-normal">
                  High-performance SPAs, modular libraries, real-time messaging layers, and decentralized web integrations.
                </p>
              </div>

              <div className="p-5 glass rounded-xl space-y-2">
                <div className="flex items-center gap-3 text-teal-600 dark:text-teal-400 font-semibold text-sm">
                  <FaLightbulb /> <span>Approach</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-normal">
                  Component-driven frontend architecture, decoupled scalable backend systems, and thorough manual/auto testing.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Grouped Skills Column */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5 space-y-8"
          >
            <div className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                Technologies
              </h2>
              <div className="h-1.5 w-12 bg-teal-500 rounded-full"></div>
            </div>

            <div className="space-y-6">
              {techGroups.map((group, index) => (
                <div key={index} className="space-y-3">
                  <h3 className="text-xs font-bold tracking-wider text-slate-400 uppercase code-font">
                    {group.category}
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {group.skills.map((skill, sIdx) => (
                      <div 
                        key={sIdx} 
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg glass text-xs font-semibold text-slate-700 dark:text-slate-300 hover:border-teal-500/40 hover:-translate-y-0.5 transition-all duration-200"
                      >
                        {skill.icon}
                        <span>{skill.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Editorial Timeline Section */}
        <section className="space-y-10 text-start pt-6">
          <div className="space-y-4">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Professional Timeline
            </h2>
            <div className="h-1.5 w-20 bg-teal-500 rounded-full"></div>
          </div>

          <div className="relative border-l-2 border-slate-200 dark:border-slate-800 ml-4 pl-8 space-y-12">
            {Experience.map((exp, index) => {
              const isOpen = openIndex === index;
              return (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative group cursor-pointer"
                  onClick={() => handleToggle(index)}
                >
                  {/* Timeline bullet node */}
                  <span className={`absolute -left-[41px] top-1.5 w-4 h-4 rounded-full border-2 border-white dark:border-slate-950 transition-all duration-300 ${
                    isOpen ? 'bg-teal-500 scale-125 shadow-lg shadow-teal-500/50' : 'bg-slate-300 dark:bg-slate-700 group-hover:bg-teal-500/50'
                  }`}></span>

                  <div className={`p-6 rounded-xl glass hover:border-teal-500/30 transition-all duration-300 ${
                    isOpen ? 'border-teal-500/30 bg-teal-500/[0.01]' : ''
                  }`}>
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                          {exp.role}
                        </h3>
                        <p className="text-sm font-semibold text-teal-600 dark:text-teal-400">
                          {exp.company}
                        </p>
                      </div>
                      <span className="text-xs code-font text-slate-400 font-medium md:self-start md:pt-1">
                        {exp.time}
                      </span>
                    </div>

                    {/* Accordion content */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 border-t border-slate-200/80 dark:border-slate-800/80 mt-4 space-y-3">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 code-font">
                              Key Contributions
                            </h4>
                            <ul className="list-disc pl-5 space-y-2 text-xs md:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                              {exp.responsibilities.map((res, rIdx) => (
                                <li key={rIdx}>{res}</li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Expand arrow indicator */}
                    <div className="flex justify-end pt-2">
                      <motion.span 
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-slate-400 group-hover:text-teal-500"
                      >
                        <FaChevronDown size={12} />
                      </motion.span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>
      </div>

      <WhatsAppLink />
    </div>
  );
}

export default AboutMe;
